import React, { useState, useEffect, useRef } from "react";
import useCollapse from "./useCollapse";
import { apiFetch, fmt } from "../api";

// ============================================================================
// VCA PAYMENT HISTORY IMPORT PANEL (TRUST-BACKOFFICE-1, 2026-05-28)
// ----------------------------------------------------------------------------
// Admin upload screen for manual backfill of VCA Payment History Report
// spreadsheets.  Pure operator-facing CRUD around four endpoints:
//
//   POST /admin/vca-import/upload?allow_create=…   (multipart .xlsx files)
//   GET  /admin/vca-import/history
//   GET  /admin/vca-import/imports/{id}
//   GET  /admin/vca-import/stale-claims
//   GET  /admin/vca-import/auto/status          (Refresh from VCA — vca_ph_autofetch.py)
//   POST /admin/vca-import/auto/start
//
// Three sub-sections:
//   1. Uploader  — drag-and-drop / click-to-pick, per-file result rows
//   2. History   — recent imports list (most recent first)
//   3. Stale     — claims with VCA-sync activity since last manual import
//                  (operator's "what to re-download next" list)
// ============================================================================
export default function VcaPaymentImport() {
  const { open, toggle, chevron } = useCollapse(false); // collapsed by default — import on demand
  const [files, setFiles] = useState([]);
  const [allowCreate, setAllowCreate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [stale, setStale] = useState([]);
  const [err, setErr] = useState(null);
  const fileInputRef = useRef(null);

  // ---------- loaders ----------
  const loadHistory = async () => {
    try { setHistory((await apiFetch("/admin/vca-import/history?limit=30")).history || []); }
    catch (e) { /* surfaced via err */ }
  };
  const loadStale = async () => {
    try { setStale((await apiFetch("/admin/vca-import/stale-claims")).stale_claims || []); }
    catch (e) { /* */ }
  };
  useEffect(() => { loadHistory(); loadStale(); }, []);

  // ---------- Refresh from VCA (auto-fetch; TRANSITIONAL — retires at VCA cutover) ----------
  const [auto, setAuto] = useState(null);        // /auto/status payload
  const [autoErr, setAutoErr] = useState(null);
  const [starting, setStarting] = useState(false);
  const wasRunning = useRef(false);
  const didAutoOpen = useRef(false);
  // lite = the 3 s progress poll: run + items only (no bucket query, no claim list on the wire);
  // counts/claims from the last full load are kept until the run ends and a full load replaces them.
  const loadAuto = async (lite) => {
    try {
      const r = await apiFetch(`/admin/vca-import/auto/status${lite ? "?lite=1" : ""}`);
      setAuto(prev => (lite && prev) ? { ...prev, run: r.run, items: r.items, configured: r.configured } : r);
    } catch (e) { /* 403 for manage_pipeline-only users — block simply doesn't render */ }
  };
  useEffect(() => { loadAuto(); }, []);
  const autoRunning = auto?.run?.status === "running";
  useEffect(() => {
    if (!autoRunning) {
      if (wasRunning.current) { wasRunning.current = false; loadAuto(); loadHistory(); loadStale(); }
      return undefined;
    }
    wasRunning.current = true;
    const t = setInterval(() => loadAuto(true), 3000);
    return () => clearInterval(t);
  }, [autoRunning]);
  // Open the panel once when there is work waiting, so the button isn't hidden behind a chevron.
  useEffect(() => {
    const c = auto?.counts;
    if (!didAutoOpen.current && c && (c.refreshable > 0 || c.needs_attention > 0 || autoRunning)) {
      didAutoOpen.current = true;
      if (!open) toggle();
    }
  }, [auto]);
  const startAuto = async (retryAttention) => {
    setStarting(true);
    setAutoErr(null);
    try {
      await apiFetch("/admin/vca-import/auto/start", {
        method: "POST",
        body: { retry_attention: !!retryAttention },
      });
    } catch (e) {
      setAutoErr(parseErr(e, "Could not start the refresh"));
    } finally {
      await loadAuto();
      setStarting(false);
    }
  };

  // ---------- file pickers ----------
  const onFileInput = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const onDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files || []).filter(f => /\.xlsx?$/i.test(f.name));
    setFiles((prev) => [...prev, ...dropped]);
  };
  const onDragOver = (e) => { e.preventDefault(); };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  const clearAll = () => setFiles([]);

  // ---------- upload ----------
  const upload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setErr(null);
    setResults([]);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name));
      fd.append("allow_create", allowCreate ? "true" : "false");
      const res = await apiFetch("/admin/vca-import/upload", {
        method: "POST",
        body: fd,
        raw: true,                          // bypass JSON serialization
      });
      const json = await res.json();
      setResults(json.results || []);
      setFiles([]);
      await loadHistory();
      await loadStale();
    } catch (e) {
      setErr(parseErr(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  // ---------- render ----------
  return (
    <div style={S.panel}>
      <div style={{...S.h, cursor:"pointer", display:"flex", alignItems:"center", gap:10}} onClick={toggle}>{chevron}VCA Payment History Import</div>
      {open && (<>
      <div style={S.sub}>
        Backfills payment category breakdowns + funding source from VCA's Payment
        History reports, and routes trust deposits + Assured IR replenishments into{" "}
        <code>trust_contributions</code>.  <b>Refresh from VCA</b> does the download and
        the import in one step; the manual upload below is the fallback for anything it
        lists under “needs attention” (strict matching by default — unmatched rows
        surface in the per-file results).
      </div>

      {auto && <AutoRefreshBlock auto={auto} running={autoRunning} starting={starting}
                                 err={autoErr} onStart={startAuto} onReload={loadAuto}/>}

      {/* Drop target — compact bar (LD-DESIGN-012 pattern 4), identical handlers */}
      <div onDrop={onDrop} onDragOver={onDragOver} style={S.drop}>
        <span style={{fontSize:13, color:"#475569"}}>
          Drag &amp; drop .xlsx files here, or
        </span>
        <button style={S.btnSec} onClick={() => fileInputRef.current?.click()}>
          Choose files
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".xlsx"
          onChange={onFileInput}
          style={{display:"none"}}
        />
      </div>

      {/* Staged files */}
      {files.length > 0 && (
        <div style={S.staged}>
          <div style={S.h2}>{files.length} file{files.length>1?"s":""} staged</div>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>File</th>
              <th style={{...S.th, width:90, textAlign:"right"}}>Size</th>
              <th style={{...S.th, width:60}}></th>
            </tr></thead>
            <tbody>
              {files.map((f, i) => (
                <tr key={i}>
                  <td style={S.td}>{f.name}</td>
                  <td style={{...S.td, textAlign:"right"}}>{(f.size/1024).toFixed(1)} kB</td>
                  <td style={S.td}>
                    <button style={S.btnLink} onClick={() => removeFile(i)}>remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{display:"flex",alignItems:"center",gap:16,marginTop:12}}>
            <label style={{fontSize:13, color:"#475569"}}>
              <input
                type="checkbox"
                checked={allowCreate}
                onChange={(e) => setAllowCreate(e.target.checked)}
                style={{marginRight:6}}
              />
              Allow creating new payments rows from spreadsheet (override strict matching)
            </label>
          </div>

          <div style={{display:"flex",gap:8,marginTop:12}}>
            <button style={{...S.btnPri, opacity: autoRunning ? 0.5 : 1}} onClick={upload}
                    disabled={uploading || autoRunning}
                    title={autoRunning ? "A Refresh from VCA is running — upload when it finishes" : undefined}>
              {uploading ? "Uploading…" : `Upload ${files.length} file${files.length>1?"s":""}`}
            </button>
            <button style={S.btnSec} onClick={clearAll} disabled={uploading}>Clear</button>
          </div>
        </div>
      )}

      {err && <div style={S.err}>{err}</div>}

      {/* Results */}
      {results.length > 0 && (
        <div style={S.section}>
          <div style={S.h2}>Upload Results</div>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>File</th>
              <th style={S.th}>Claim</th>
              <th style={S.th}>Match</th>
              <th style={S.thN}>Total</th>
              <th style={S.thN}>Matched</th>
              <th style={S.thN}>Updated</th>
              <th style={S.thN}>Unmatched</th>
              <th style={S.thN}>Created</th>
              <th style={S.thN}>Deposits</th>
              <th style={S.thN}>IRs</th>
              <th style={S.th}>Notes</th>
            </tr></thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i}>
                  <td style={S.td}>{r.file_name}</td>
                  <td style={S.td}>
                    {r.claim_id
                      ? <span>#{r.claim_id} <span style={{color:"#64748b"}}>{r.claim_number_from_file}</span></span>
                      : <span style={{color:"#dc2626"}}>{r.claim_number_from_file || "—"}</span>}
                  </td>
                  <td style={S.td}>{badgeForMatch(r.claim_resolution)}</td>
                  <td style={S.tdN}>{r.rows_total ?? "—"}</td>
                  <td style={S.tdN}>{r.rows_matched ?? "—"}</td>
                  <td style={S.tdN}>{r.rows_updated ?? "—"}</td>
                  <td style={{...S.tdN, color: r.rows_unmatched ? "#dc2626" : "#94a3b8"}}>{r.rows_unmatched ?? "—"}</td>
                  <td style={S.tdN}>{r.rows_created ?? "—"}</td>
                  <td style={S.tdN}>
                    {r.deposits_created ?? r.deposits_routed ?? "—"}
                    {r.deposits_skipped_dup > 0 &&
                      <span style={{color:"#64748b",fontSize:11}}> (+{r.deposits_skipped_dup} dup)</span>}
                  </td>
                  <td style={S.tdN}>
                    {r.received_reimbursements_created ?? r.received_reimbursements_routed ?? "—"}
                    {r.received_reimbursements_skipped_dup > 0 &&
                      <span style={{color:"#64748b",fontSize:11}}> (+{r.received_reimbursements_skipped_dup} dup)</span>}
                  </td>
                  <td style={S.td}>
                    {r.error && <span style={{color:"#dc2626"}}>{r.error}</span>}
                    {r.deposits_unmatched_underwriter > 0 &&
                      <span style={{color:"#d97706",fontSize:12}}>
                        {r.deposits_unmatched_underwriter} deposit{r.deposits_unmatched_underwriter>1?"s":""} unmatched co.
                      </span>}
                    {r.received_reimbursements_unmatched > 0 &&
                      <span style={{color:"#d97706",fontSize:12, marginLeft:6}}>
                        {r.received_reimbursements_unmatched} IR{r.received_reimbursements_unmatched>1?"s":""} unmatched co.
                      </span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stale claims */}
      <div style={S.section}>
        <div style={S.h2}>
          Stale Claims
          <span style={{fontSize:12, color:"#64748b", fontWeight:"normal", marginLeft:8}}>
            ({stale.length}) — claims with VCA-sync activity since their last import
          </span>
          <button style={{...S.btnLink, marginLeft:12}} onClick={loadStale}>refresh</button>
        </div>
        {/* Action buttons live OUTSIDE the empty/non-empty branch so they're
            always visible (just disabled when the list is empty). */}
        <div style={{fontSize:12, color:"#64748b", marginBottom:8}}>
          Refresh from VCA (above) handles these automatically. Manual fallback only:
          <button
            style={{...S.btnLink, marginLeft:8, opacity: stale.length===0 ? 0.4 : 1}}
            disabled={stale.length===0}
            onClick={() => navigator.clipboard.writeText(stale.map(s => s.claim_number).join("\n"))}>
            copy all claim numbers
          </button>
          <button
            style={{...S.btnLink, marginLeft:8, opacity: stale.length===0 ? 0.4 : 1}}
            disabled={stale.length===0}
            onClick={() => downloadCsv(stale.map(s => s.claim_number), "stale_claims.csv")}>
            download as CSV
          </button>
          <span style={{marginLeft:8, color:"#94a3b8"}}>
            (one claim number per line)
          </span>
        </div>
        {stale.length === 0 ? (
          <div style={{color:"#64748b", fontSize:13, padding:"8px 0"}}>
            All caught up — no claims have new payment activity since their last import.
          </div>
        ) : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Claim</th>
              <th style={S.th}>Last Payment Update</th>
              <th style={S.th}>Last Import Anchor</th>
              <th style={S.th}>Last Uploaded</th>
            </tr></thead>
            <tbody>
              {stale.map((s) => (
                <tr key={s.id}>
                  <td style={S.td}>{s.claim_number}</td>
                  <td style={S.td}>{fmt.datetime(s.last_pmt_updated)}</td>
                  <td style={S.td}>{fmt.datetime(s.last_import_anchor)}</td>
                  <td style={S.td}>{fmt.datetime(s.last_uploaded_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* History */}
      <div style={S.section}>
        <div style={S.h2}>
          Recent Imports
          <button style={{...S.btnLink, marginLeft:12}} onClick={loadHistory}>refresh</button>
        </div>
        {history.length === 0 ? (
          <div style={{color:"#64748b", fontSize:13, padding:"8px 0"}}>No imports yet.</div>
        ) : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>When</th>
              <th style={S.th}>Who</th>
              <th style={S.th}>File</th>
              <th style={S.th}>Claim</th>
              <th style={S.thN}>Total</th>
              <th style={S.thN}>Upd</th>
              <th style={S.thN}>Unmatch</th>
              <th style={S.thN}>New</th>
              <th style={S.thN}>Dep</th>
              <th style={S.th}>Mode</th>
            </tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td style={S.td}>{fmt.datetime(h.uploaded_at)}</td>
                  <td style={S.td}>{h.uploaded_by_name || `#${h.uploaded_by}`}</td>
                  <td style={S.td}>{h.file_name}</td>
                  <td style={S.td}>
                    {h.claim_number
                      ? h.claim_number
                      : <span style={{color:"#dc2626"}}>{h.claim_number_from_file || "—"}</span>}
                  </td>
                  <td style={S.tdN}>{h.rows_total}</td>
                  <td style={S.tdN}>{h.rows_updated}</td>
                  <td style={{...S.tdN, color: h.rows_unmatched ? "#dc2626" : "#94a3b8"}}>
                    {h.rows_unmatched}
                  </td>
                  <td style={S.tdN}>{h.rows_created}</td>
                  <td style={S.tdN}>{h.deposits_routed}</td>
                  <td style={S.td}>
                    {h.allow_create_new_payments
                      ? <span style={{color:"#d97706",fontSize:12}}>allow-create</span>
                      : <span style={{color:"#64748b",fontSize:12}}>strict</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>)}
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────
function parseErr(e, fallback) {
  try { return JSON.parse(e.message).detail || fallback; } catch { return fallback; }
}
// Generate a one-column CSV (one value per line) and trigger a browser
// download.  Used by Stale Claims to feed the local Selenium scraper.
function downloadCsv(values, filename) {
  if (!values || values.length === 0) return;
  const csv = values.join("\n") + "\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
function badgeForMatch(kind) {
  if (!kind) return null;
  const map = {
    exact:                 { label: "exact",            color: "#10b981" },
    suffix_stripped:       { label: "suffix-stripped",  color: "#3b82f6" },
    unmatched_ambiguous:   { label: "ambiguous",        color: "#d97706" },
    unmatched_none:        { label: "no match",         color: "#dc2626" },
    no_claim_number_in_file:{ label:"no claim# in file",color:"#dc2626" },
  };
  const c = map[kind] || { label: kind, color: "#64748b" };
  return (
    <span style={{
      display:"inline-block", padding:"2px 8px", borderRadius:10,
      backgroundColor: c.color + "22", color: c.color, fontSize:11, fontWeight:600,
    }}>{c.label}</span>
  );
}

// ─── styles (inline to keep this self-contained) ──────────────────
// ============================================================================
// Refresh from VCA — one motion instead of download-then-upload.
// Outcome labels are display-only; the stored codes live in vca_ph_autofetch.py.
// ============================================================================
const AUTO_OUTCOME = {
  pending:        { label: "waiting",                         tone: "#64748b" },
  ok:             { label: "imported",                        tone: "#047857" },
  invalid:        { label: "VCA doesn't recognise this claim", tone: "#b45309" },
  empty:          { label: "VCA returned no report",          tone: "#b45309" },
  wrong_claim:    { label: "report didn't match this claim — nothing imported", tone: "#b91c1c" },
  ambiguous:      { label: "two VCA claims share this name — nothing imported", tone: "#b91c1c" },
  review_creates: { label: "would create more than 10 payments — held; upload by hand to confirm", tone: "#b45309" },
  import_error:   { label: "VCA's report couldn't be read or imported — nothing imported; needs a look", tone: "#b91c1c" },
  error:          { label: "connection problem — will retry next run", tone: "#b45309" },
};
const AUTO_RUN_ERROR = {
  login_rejected: "VCA rejected the saved login — the password may have changed.",
  session_lost:   "VCA kept logging the session out.",
  network:        "Couldn't reach VCA.",
  http_status:    "VCA returned an error.",
  interrupted:    "The run was interrupted (server restart). Press Refresh to pick up where it stopped.",
};

function AutoRefreshBlock({ auto, running, starting, err, onStart, onReload }) {
  const c = auto.counts || {};
  const run = auto.run;
  const items = auto.items || [];
  const done = items.filter(i => i.status !== "pending").length;
  const okN = items.filter(i => i.status === "ok").length;
  const bad = items.filter(i => i.status !== "ok" && i.status !== "pending");
  const attention = (auto.claims || []).filter(x => x.needs_attention);
  const canStart = auto.configured && !running && !starting && c.refreshable > 0;
  return (
    <div style={{...S.section, marginTop:0, padding:"12px 14px", border:"1px solid #cbd5e1",
                 borderRadius:6, background:"#f8fafc"}}>
      <div style={{display:"flex", alignItems:"center", gap:12, flexWrap:"wrap"}}>
        <button style={{...S.btnPri, opacity: canStart ? 1 : 0.5}} disabled={!canStart}
                onClick={() => onStart(false)}>
          {running ? "Refreshing…" : starting ? "Starting…" : `Refresh ${c.refreshable || 0} from VCA`}
        </button>
        <span style={{fontSize:13, color:"#334155"}}>
          <b>{c.changed || 0}</b> changed since last import · <b>{c.never_imported || 0}</b> never imported
          {c.needs_attention > 0 && <> · <b style={{color:"#b45309"}}>{c.needs_attention}</b> need attention</>}
        </span>
        {!running && <button style={S.btnLink} onClick={onReload}>refresh</button>}
      </div>
      <div style={{fontSize:12, color:"#64748b", marginTop:6}}>
        Pulls each claim's Payment History report straight from VCA and imports it (creates missing
        payments, same as ticking “allow create”). Nothing is downloaded to your computer.
      </div>
      {!auto.configured && (
        <div style={{...S.err, marginTop:8}}>
          {auto.cred_state === "unreadable" ? "The VCA login file exists on the server but the app can't read it (permissions)."
           : auto.cred_state === "incomplete" ? "The VCA login file on the server is missing a value — re-run the installer."
           : "VCA web login is not configured on the server yet — the button is off until it is."}
        </div>
      )}
      {err && <div style={{...S.err, marginTop:8}}>{err}</div>}

      {run && (running || run.finished_at) && (
        <div style={{marginTop:10, fontSize:13}}>
          <b>{running ? "Running" : run.status === "done" ? "Last run" : "Last run stopped"}</b>
          {" — "}{done} / {run.claims_total} · {okN} imported
          {bad.length > 0 && <> · <span style={{color:"#b45309"}}>{bad.length} not imported</span></>}
          {!running && run.finished_at && <span style={{color:"#64748b"}}> · {fmt.datetime(run.finished_at)}{run.started_by_name ? ` · ${run.started_by_name}` : ""}</span>}
          {run.status === "failed" && (
            <div style={{...S.err, marginTop:6}}>{AUTO_RUN_ERROR[run.error] || "The run stopped unexpectedly."}</div>
          )}
          {running && (
            <div style={{height:6, background:"#e2e8f0", borderRadius:3, marginTop:6}}>
              <div style={{height:6, borderRadius:3, background:"#0f172a",
                           width: `${run.claims_total ? Math.round(done / run.claims_total * 100) : 0}%`}}/>
            </div>
          )}
        </div>
      )}

      {bad.length > 0 && (
        <table style={{...S.table, marginTop:8}}>
          <thead><tr>
            <th style={S.th}>Not imported this run</th>
            <th style={S.th}>Why</th>
          </tr></thead>
          <tbody>
            {bad.map(i => (
              <tr key={i.id}>
                <td style={S.td}>{i.claim_number || `claim #${i.claim_id}`}</td>
                <td style={{...S.td, color:(AUTO_OUTCOME[i.status] || {}).tone}}>{(AUTO_OUTCOME[i.status] || {label:i.status}).label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {attention.length > 0 && !running && (
        <div style={{marginTop:10, fontSize:12, color:"#64748b"}}>
          <b style={{color:"#b45309"}}>{attention.length} need attention</b> — VCA wouldn't hand these over
          automatically, so they're left out of the count until their payments change again. Download the report
          from VCA and upload it below, or{" "}
          <button style={S.btnLink} disabled={!auto.configured || starting || running} onClick={() => onStart(true)}>try them again</button>.
          <div style={{marginTop:4}}>
            {attention.map(x => (
              <span key={x.id} style={{display:"inline-block", marginRight:12}}>
                {x.claim_number} <span style={{color:"#94a3b8"}}>({(AUTO_OUTCOME[x.last_attempt_status] || {label:""}).label.split(" — ")[0]})</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  panel:   { background:"#fff", padding:"20px 24px", border:"1px solid #e2e8f0", borderRadius:8, marginBottom:16 },
  h:       { fontSize:18, fontWeight:600, color:"#0f172a", marginBottom:6 },
  h2:      { fontSize:14, fontWeight:600, color:"#0f172a", marginTop:16, marginBottom:8 },
  sub:     { fontSize:13, color:"#64748b", marginBottom:14, lineHeight:1.5 },
  drop:    { border:"1px dashed #cbd5e1", borderRadius:6, padding:"8px 14px",
             display:"flex", alignItems:"center", justifyContent:"center", gap:10,
             flexWrap:"wrap", maxHeight:200, marginBottom:12, background:"#f8fafc" },
  staged:  { padding:"12px", background:"#f1f5f9", borderRadius:6, marginBottom:12 },
  section: { marginTop:24, paddingTop:16, borderTop:"1px solid #e2e8f0" },
  table:   { width:"100%", borderCollapse:"collapse", fontSize:13 },
  th:      { textAlign:"left", padding:"6px 8px", borderBottom:"1px solid #e2e8f0",
             color:"#475569", fontWeight:600, fontSize:12 },
  thN:     { textAlign:"right", padding:"6px 8px", borderBottom:"1px solid #e2e8f0",
             color:"#475569", fontWeight:600, fontSize:12 },
  td:      { padding:"6px 8px", borderBottom:"1px solid #f1f5f9", verticalAlign:"top" },
  tdN:     { padding:"6px 8px", borderBottom:"1px solid #f1f5f9", textAlign:"right" },
  btnPri:  { padding:"8px 16px", background:"#0f172a", color:"#fff",
             border:"none", borderRadius:6, cursor:"pointer", fontSize:13, fontWeight:500 },
  btnSec:  { padding:"8px 16px", background:"#fff", color:"#0f172a",
             border:"1px solid #cbd5e1", borderRadius:6, cursor:"pointer", fontSize:13 },
  btnLink: { padding:0, background:"transparent", color:"#0369a1",
             border:"none", cursor:"pointer", fontSize:12, textDecoration:"underline" },
  err:     { background:"#fee2e2", color:"#991b1b", padding:"8px 12px",
             borderRadius:6, marginTop:12, fontSize:13 },
};
